import { t } from '../../i18n/es';
import type { LevelRun } from '../../core/level-runner';
import { hudModel, type HudModel } from './hud-model';
import { routeTelemetry } from './telemetry';
import { powerUpBadges } from './badges-model';
import { bossHealthBar } from './boss-bar';
import { bossReactionBubble } from './boss-reaction';
import { jumpPrompt } from './jump-prompt';

export interface HudOptions {
  levelId: () => string | null;
  run: () => LevelRun | null;
  onPause: () => void;
}

/**
 * Flight HUD (GDD §13.3, issue #58): VIDAS and FAJOS top-left, the operation top-center and PUNTOS with the
 * pause button top-right, the route telemetry under the operation (#59) and the power-up badges bottom-centre
 * (#82), anchored to the edges of the 16:9 view and outside its central third. It only
 * reads the core, once per animation frame, and touches the DOM only when a value changes. Only the lives
 * are announced (aria-live polite). Native elements until the design system lands (#15).
 */
export function flightHud(options: HudOptions): HTMLElement {
  const hud = document.createElement('div');
  hud.className = 'hud';
  hud.innerHTML = `
    <div class="hud-zone hud-left">
      <span class="readout" data-hud="lives" aria-live="polite"></span>
      <span class="readout" data-hud="dollars"></span>
    </div>
    <div class="hud-zone hud-center"><span class="tactical" data-hud="operation"></span></div>
    <div class="hud-zone hud-right">
      <span class="readout" data-hud="score"></span>
      <button type="button" class="button" data-action="pause">${t('ui.pausa.open')}</button>
    </div>
    <div class="hud-zone hud-bottom" data-hud="badges" aria-live="polite" aria-relevant="additions removals"></div>`;
  hud.querySelector('[data-action="pause"]')!.addEventListener('click', options.onPause);
  // The route telemetry goes under the operation (#59).
  const telemetry = routeTelemetry();
  hud.querySelector('.hud-center')!.append(telemetry.element);
  // The boss health bar goes under the telemetry, only in the arena (#91).
  const bossBar = bossHealthBar();
  hud.querySelector('.hud-center')!.append(bossBar.element);
  // The comic reaction of the boss to a decoy hit (#96).
  const reaction = bossReactionBubble();
  hud.append(reaction.element);
  // The SALTAR prompt of El Salto (#94).
  const prompt = jumpPrompt();
  hud.append(prompt.element);

  const fields = Object.fromEntries(
    (['lives', 'dollars', 'score', 'operation'] as const).map((key) => [key, hud.querySelector<HTMLElement>(`[data-hud="${key}"]`)!]),
  ) as Record<keyof HudModel, HTMLElement>;

  // Power-up badges (#82): cr-art-ds-badge elements, added and removed with the power-ups; only their
  // appearance and removal are announced, not every tenth of the countdown.
  const badgeZone = hud.querySelector<HTMLElement>('[data-hud="badges"]')!;
  const updateBadges = (core: { armor: boolean; bribeRemaining: number }) => {
    const wanted = powerUpBadges(core);
    for (const element of [...badgeZone.children] as HTMLElement[]) {
      if (!wanted.some((badge) => badge.id === element.dataset.badge)) element.remove();
    }
    for (const badge of wanted) {
      let element = badgeZone.querySelector<HTMLElement>(`[data-badge="${badge.id}"]`);
      if (!element) {
        element = document.createElement('cr-art-ds-badge');
        element.className = 'badge readout';
        element.dataset.badge = badge.id;
        badgeZone.append(element);
      }
      if (element.textContent !== badge.text) element.textContent = badge.text;
    }
  };

  const update = () => {
    const run = options.run();
    const levelId = options.levelId();
    if (!run || !levelId) return;
    telemetry.update(run);
    bossBar.update(run.boss);
    reaction.update(run);
    prompt.update(run);
    updateBadges(run.core);
    const model = hudModel(levelId, run.core);
    for (const key of Object.keys(fields) as (keyof HudModel)[]) {
      if (fields[key].textContent !== model[key]) fields[key].textContent = model[key];
    }
  };
  const frame = () => {
    if (!hud.isConnected) return;
    update();
    requestAnimationFrame(frame);
  };
  update();
  requestAnimationFrame(frame);
  return hud;
}
